import React, { useState, useEffect, useCallback } from 'react'
import { format, parseISO, isValid } from 'date-fns'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { fetchReports, fetchReportPhotos, getPhotoUrl } from '../lib/supabase'
import { AlertTriangle, CheckCircle, Search, Calendar, RefreshCw, ImageIcon } from 'lucide-react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'

const ReportsList = ({ merchantId, storeId, locationNr }) => {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all') // 'all', 'damage_found', 'no_damage'
  const [reportPhotos, setReportPhotos] = useState({})
  const [photoLoading, setPhotoLoading] = useState({})
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [dataFetched, setDataFetched] = useState(false)

  // Load reports when props change
  const loadReports = useCallback(async () => {
    console.log("loadReports called with merchantId:", merchantId)
    setLoading(true)
    setError(null)
    
    try {
      if (!merchantId) {
        console.log("No merchant ID provided, cannot load reports")
        setReports([])
        setLoading(false)
        setDataFetched(true)
        return
      }
      
      // Only pass location parameter to API if it's not '*'
      const locationFilter = locationNr === '*' ? null : locationNr
      console.log('Fetching reports with filters:', { merchantId, storeId, locationNr: locationFilter })
      
      try {
        // Add debugging output to track the request
        console.log(`Starting fetchReports call to Supabase at ${new Date().toISOString()}`);
        const data = await fetchReports(merchantId, storeId, locationFilter)
        console.log(`Completed fetchReports call at ${new Date().toISOString()}`);
        console.log('Fetched reports:', data ? data.length : 0);
        
        if (Array.isArray(data)) {
          setReports(data)
          console.log("Reports state updated with", data.length, "items");
          console.log("First report date:", data.length > 0 ? 
            { created_at: data[0].created_at, created_at_vienna: data[0].created_at_vienna } : 
            "No reports");
          
          // Initialize photo loading states for all reports
          const newPhotoLoading = {};
          data.forEach(report => {
            newPhotoLoading[report.order_id] = true;
          });
          setPhotoLoading(newPhotoLoading);
          
          // Load photos for all visible reports
          loadPhotosForReports(data);
        } else {
          console.error("fetchReports did not return an array:", data);
          setReports([]);
        }
      } catch (fetchError) {
        console.error('Error in fetchReports:', fetchError)
        setReports([]) // Ensure reports is always an array
        setError('Failed to fetch reports. Please try again.')
      }
      
      setDataFetched(true)
    } catch (err) {
      console.error('Failed to fetch reports:', err)
      setError('Failed to load reports. Please try again.')
      setReports([]) // Ensure reports is always an array
    } finally {
      setLoading(false)
    }
  }, [merchantId, storeId, locationNr])

  // Load photos for all reports - improved with better error handling and retry logic
  const loadPhotosForReports = async (reports) => {
    if (!reports || reports.length === 0) return;
    
    // Create a new photo map object
    const photoMap = { ...reportPhotos };
    
    // Process all reports, but with a small delay between batches to avoid overwhelming
    const batchSize = 4;
    const maxRetries = 2;
    
    for (let i = 0; i < reports.length; i += batchSize) {
      const batch = reports.slice(i, i + batchSize);
      
      // Process batch in parallel for better performance
      await Promise.all(batch.map(async (report) => {
        // Skip if we already have a photo for this report
        if (photoMap[report.order_id]) {
          setPhotoLoading(prev => ({ ...prev, [report.order_id]: false }));
          return;
        }
        
        let retries = 0;
        while (retries <= maxRetries) {
          try {
            // Fetch photos for this report
            const photos = await fetchReportPhotos(report.order_id, report.merchant_id);
            
            // If we got photos, try to get the URL for the first one
            if (photos && photos.length > 0) {
              const url = await getPhotoUrl(photos[0].file_path);
              if (url) {
                // Update the photo map with this URL
                photoMap[report.order_id] = url;
                
                // Update state with the new photo
                setReportPhotos(prevPhotos => ({
                  ...prevPhotos,
                  [report.order_id]: url
                }));
              }
            }
            
            // Mark loading as complete for this report
            setPhotoLoading(prev => ({ ...prev, [report.order_id]: false }));
            
            // Success, no need to retry
            break;
          } catch (error) {
            console.error(`Error loading photo for order ${report.order_id} (attempt ${retries + 1}):`, error);
            retries++;
            
            if (retries > maxRetries) {
              // Mark loading as complete even if it failed
              setPhotoLoading(prev => ({ ...prev, [report.order_id]: false }));
              console.warn(`Failed to load photo for order ${report.order_id} after ${maxRetries} attempts`);
            } else {
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }
      }));
      
      // Add a small delay between batches to prevent rate limiting
      if (i + batchSize < reports.length) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
  };

  // Load reports when dependencies change
  useEffect(() => {
    console.log("Effect triggered to load reports");
    try {
      loadReports()
    } catch (err) {
      console.error('Error in loadReports effect:', err)
      setError('Failed to load reports. Please try again.')
      setLoading(false)
    }
  }, [loadReports])

  // Enhanced safe format date function that won't crash on invalid dates
  const safeFormatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown date';
      
      // Create a date object from the string
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateString}`);
        return 'Invalid date';
      }
      
      // Format the date
      return format(date, 'yyyy-MM-dd HH:mm');
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateString);
      return 'Date error';
    }
  }

  // Filter reports based on search and status filter - with additional safety checks
  const filteredReports = React.useMemo(() => {
    try {
      if (!Array.isArray(reports)) {
        console.error('Reports is not an array:', reports)
        return []
      }
      
      return reports.filter(report => {
        if (!report) return false
        
        try {
          const orderId = report.order_id?.toLowerCase() || ''
          const description = report.description?.toLowerCase() || ''
          const searchTermLower = searchTerm.toLowerCase()
          
          const matchesSearch = orderId.includes(searchTermLower) || 
                               description.includes(searchTermLower)
          
          if (filter === 'all') return matchesSearch
          return matchesSearch && report.status === filter
        } catch (filterError) {
          console.error('Error filtering report:', filterError, report)
          return false
        }
      })
    } catch (err) {
      console.error('Error in filteredReports:', err)
      return []
    }
  }, [reports, searchTerm, filter])

  const handleRefresh = () => {
    loadReports()
  }

  // Handler for opening full-size photo in new tab
  const openFullSizePhoto = (photoUrl, e) => {
    if (!photoUrl) return
    try {
      if (e) e.stopPropagation() // Prevent event bubbling
      window.open(photoUrl, '_blank')
    } catch (error) {
      console.error('Error opening photo:', error)
    }
  }

  // Format description for mobile view
  const truncateDescription = (description, maxLength = 50) => {
    if (!description) return '-'
    if (description.length <= maxLength) return description
    return description.substring(0, maxLength) + '...'
  }

  // Preserve params when navigating to detail view
  const navigateToDetail = (report) => {
    // Get current parameters
    const params = new URLSearchParams();
    
    // Add merchant ID if available
    if (merchantId) {
      params.set('mid', merchantId);
    } else if (report.merchant_id) {
      params.set('mid', report.merchant_id);
    }
    
    // Add location if specified
    if (locationNr && locationNr !== '*') {
      params.set('loc', locationNr);
    }
    
    // Add store ID if specified
    if (storeId) {
      params.set('sid', storeId);
    }
    
    // Navigate to detail page with state and hash parameters
    navigate(`/report/${report.order_id}`, {
      state: { report },
      hash: params.toString() ? `?${params.toString()}` : ''
    });
  };

  // Safe render method to catch any rendering errors
  const renderSafely = () => {
    try {
      if (loading && !dataFetched) {
        return (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )
      }

      if (error) {
        return (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
            <button
              onClick={handleRefresh}
              className="mt-3 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
            >
              Try Again
            </button>
          </div>
        )
      }

      if (!merchantId) {
        return (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Merchant Selected</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Please specify a merchant ID in the URL using one of these parameters:
              <br />
              <code className="text-sm font-mono mt-2 block bg-gray-100 p-2 rounded">
                ?mid=merchant123 or ?merchant_id=merchant123
              </code>
              <span className="text-sm text-gray-500 mt-3 block font-bold">
                For HashRouter, try using: <code className="font-mono">#/?mid=merchant123</code>
              </span>
            </p>
          </div>
        )
      }

      return (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by order ID or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    filter === 'all'
                      ? 'bg-gray-200 text-gray-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('damage_found')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    filter === 'damage_found'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className="flex items-center">
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="mr-1 text-orange-500" />
                    Issues
                  </span>
                </button>
                <button
                  onClick={() => setFilter('no_damage')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    filter === 'no_damage'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className="flex items-center">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-1 text-green-500" />
                    Perfect
                  </span>
                </button>
              </div>
              <button
                onClick={handleRefresh}
                className="p-2 rounded-full hover:bg-gray-100"
                title="Refresh reports"
              >
                <RefreshCw className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {filteredReports.length === 0 ? (
            <div className="text-center py-8">
              <SafeIcon icon={FiIcons.FiInbox} className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No reports found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || filter !== 'all' || storeId || (locationNr && locationNr !== '*')
                  ? 'Try adjusting your search or filter criteria'
                  : 'No reports have been submitted yet'}
              </p>
              <button onClick={handleRefresh} className="mt-4 btn-secondary text-sm px-4 py-2">
                <span className="flex items-center">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </span>
              </button>
            </div>
          ) : (
            <>
              {/* Desktop view - hidden on mobile */}
              <div className="hidden md:block overflow-x-auto">
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="px-6">Order ID</th>
                        <th className="px-6">Status</th>
                        <th className="px-12 min-w-[180px]">Date</th>
                        <th className="px-6">Photo</th>
                        <th className="w-1/3 px-6">Description</th>
                        <th className="px-6">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReports.map((report) => {
                        // Generate a stable key for each row
                        const rowKey = report.id || report.order_id || `report-${Math.random().toString(36).substring(7)}`
                        const isPhotoLoading = photoLoading[report.order_id];
                        
                        // Use created_at_vienna if available, otherwise fall back to created_at
                        const dateToShow = report.created_at_vienna || report.created_at;
                        
                        return (
                          <tr key={rowKey}>
                            <td className="font-medium px-6">
                              <a
                                href={`https://admin.twicecommerce.com/booking/${report.order_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-800 hover:underline"
                              >
                                {report.order_id}
                              </a>
                            </td>
                            <td className="px-6">
                              {report.status === 'damage_found' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  <SafeIcon icon={FiIcons.FiAlertTriangle} className="mr-1" />
                                  Issues
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-1" />
                                  Perfect
                                </span>
                              )}
                            </td>
                            <td className="text-gray-500 px-12 min-w-[180px] whitespace-nowrap">
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {safeFormatDate(dateToShow)}
                              </div>
                            </td>
                            <td className="px-6">
                              {isPhotoLoading ? (
                                <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                                </div>
                              ) : reportPhotos[report.order_id] ? (
                                <div
                                  className="relative w-16 h-16 bg-gray-100 rounded-md overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={(e) => openFullSizePhoto(reportPhotos[report.order_id], e)}
                                  title="Click to view full size"
                                >
                                  <img
                                    src={reportPhotos[report.order_id]}
                                    alt="Report Photo"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.onerror = null
                                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpolyline points='21 15 16 10 5 21'/%3E%3C/svg%3E"
                                    }}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black bg-opacity-20 transition-opacity">
                                    <SafeIcon icon={FiIcons.FiMaximize2} className="text-white w-6 h-6" />
                                  </div>
                                </div>
                              ) : (
                                <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center">
                                  <ImageIcon className="w-8 h-8 text-gray-400" />
                                </div>
                              )}
                            </td>
                            <td className="w-1/3 px-6">
                              <div className="break-words">{report.description || '-'}</div>
                            </td>
                            <td className="px-6">
                              <button
                                onClick={() => navigateToDetail(report)}
                                className="text-primary-600 hover:text-primary-800 font-medium"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile view - card layout */}
              <div className="md:hidden space-y-4">
                {filteredReports.map((report) => {
                  // Generate a stable key for each card
                  const cardKey = report.id || report.order_id || `report-mobile-${Math.random().toString(36).substring(7)}`
                  const isPhotoLoading = photoLoading[report.order_id];
                  
                  // Use created_at_vienna if available, otherwise fall back to created_at
                  const dateToShow = report.created_at_vienna || report.created_at;
                  
                  return (
                    <div key={cardKey} className="card p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <a
                            href={`https://admin.twicecommerce.com/booking/${report.order_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                          >
                            {report.order_id}
                          </a>
                          <div className="text-gray-500 text-sm flex items-center mt-1">
                            <Calendar className="w-3.5 h-3.5 mr-1" />
                            {safeFormatDate(dateToShow)}
                          </div>
                        </div>
                        
                        {report.status === 'damage_found' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            <SafeIcon icon={FiIcons.FiAlertTriangle} className="mr-1" />
                            Issues
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-1" />
                            Perfect
                          </span>
                        )}
                      </div>
                      
                      <div className="flex space-x-3 mb-3">
                        {isPhotoLoading ? (
                          <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                          </div>
                        ) : reportPhotos[report.order_id] ? (
                          <div
                            className="relative w-16 h-16 bg-gray-100 rounded-md overflow-hidden cursor-pointer flex-shrink-0"
                            onClick={(e) => openFullSizePhoto(reportPhotos[report.order_id], e)}
                          >
                            <img
                              src={reportPhotos[report.order_id]}
                              alt="Report Photo"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null
                                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpolyline points='21 15 16 10 5 21'/%3E%3C/svg%3E"
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="text-sm text-gray-600 line-clamp-2">{report.description || '-'}</div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end">
                        <button
                          onClick={() => navigateToDetail(report)}
                          className="text-primary-600 hover:text-primary-800 font-medium text-sm"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )
    } catch (renderError) {
      console.error('Error rendering ReportsList:', renderError)
      return (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>An error occurred while displaying reports. Please try refreshing the page.</span>
          </div>
          <div className="mt-2 text-sm text-red-600">
            Error details: {renderError.message || "Unknown error"}
          </div>
          <button onClick={handleRefresh} className="mt-3 btn-primary text-sm px-3 py-1">
            Refresh
          </button>
        </div>
      )
    }
  }

  // Use try-catch wrapper around the entire component render
  try {
    return <React.Fragment>{renderSafely()}</React.Fragment>
  } catch (error) {
    console.error('Fatal error in ReportsList component:', error)
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 mr-2" />
          <span>A critical error occurred. Please refresh the page or contact support.</span>
        </div>
        <button onClick={() => window.location.reload()} className="mt-3 bg-red-600 text-white px-3 py-1 rounded">
          Reload Page
        </button>
      </div>
    )
  }
}

export default ReportsList